// Single source of truth for the Colorado Springs Coverage Map.
// Coordinates are in an 800x600 SVG viewBox, with our Woodmen Rd HQ
// pinned at (400, 300). Positions reflect rough compass bearings —
// not survey-accurate, but directionally faithful so the map "reads"
// correctly to a local.

export type RingTier = "in-city" | "eastern-plains" | "pikes-peak" | "extended";

export type CoverageRegion = {
  name: string;
  tier: RingTier;
  x: number;
  y: number;
  direction: string; // e.g. "NE of HQ"
  drive: string; // e.g. "~10 min"
  anchor: string; // in-page hash id on /colorado-springs-seo
};

export const HQ = {
  x: 400,
  y: 300,
  label: "HQ — 3210 E Woodmen Rd",
};

// Approx radii in px on the 800x600 viewBox.
// 15 mi ≈ 150px, 30 mi ≈ 300px (scaled to fit).
export const RING_15 = 150;
export const RING_30 = 300;

export const COVERAGE_REGIONS: CoverageRegion[] = [
  // ── In Colorado Springs ──────────────────────────────────
  { name: "Briargate", tier: "in-city", x: 395, y: 250, direction: "Adjacent N", drive: "~5 min", anchor: "in-city" },
  { name: "Northgate", tier: "in-city", x: 410, y: 210, direction: "N of HQ", drive: "~8 min", anchor: "in-city" },
  { name: "Flying Horse", tier: "in-city", x: 445, y: 195, direction: "NNE of HQ", drive: "~10 min", anchor: "in-city" },
  { name: "Cordera", tier: "in-city", x: 370, y: 225, direction: "N of HQ", drive: "~6 min", anchor: "in-city" },
  { name: "Wolf Ranch", tier: "in-city", x: 455, y: 240, direction: "NE of HQ", drive: "~7 min", anchor: "in-city" },
  { name: "Powers Corridor", tier: "in-city", x: 490, y: 340, direction: "SE of HQ", drive: "~10 min", anchor: "in-city" },
  { name: "Stetson Hills", tier: "in-city", x: 505, y: 295, direction: "E of HQ", drive: "~8 min", anchor: "in-city" },
  { name: "Black Forest", tier: "in-city", x: 490, y: 215, direction: "NE of HQ", drive: "~12 min", anchor: "in-city" },
  { name: "Old Colorado City", tier: "in-city", x: 295, y: 365, direction: "SW of HQ", drive: "~15 min", anchor: "in-city" },
  { name: "Garden of the Gods", tier: "in-city", x: 290, y: 295, direction: "W of HQ", drive: "~12 min", anchor: "in-city" },
  { name: "Broadmoor", tier: "in-city", x: 345, y: 415, direction: "S of HQ", drive: "~18 min", anchor: "in-city" },
  { name: "Cimarron Hills", tier: "in-city", x: 545, y: 320, direction: "E of HQ", drive: "~12 min", anchor: "in-city" },

  // ── Eastern El Paso County plains ────────────────────────
  { name: "Falcon", tier: "eastern-plains", x: 590, y: 275, direction: "E of HQ", drive: "~15 min", anchor: "eastern-plains" },
  { name: "Peyton", tier: "eastern-plains", x: 635, y: 255, direction: "E of HQ", drive: "~20 min", anchor: "eastern-plains" },
  { name: "Percheron", tier: "eastern-plains", x: 555, y: 205, direction: "NE of HQ", drive: "~18 min", anchor: "eastern-plains" },
  { name: "Ellicott (Mayberry)", tier: "eastern-plains", x: 670, y: 345, direction: "ESE of HQ", drive: "~25 min", anchor: "eastern-plains" },
  { name: "Calhan", tier: "eastern-plains", x: 710, y: 255, direction: "E of HQ", drive: "~30 min", anchor: "eastern-plains" },

  // ── Pikes Peak Region (inner 15mi) ───────────────────────
  { name: "Monument", tier: "pikes-peak", x: 375, y: 145, direction: "N of HQ", drive: "~18 min", anchor: "pikes-peak-region" },
  { name: "Fountain", tier: "pikes-peak", x: 395, y: 465, direction: "S of HQ", drive: "~22 min", anchor: "pikes-peak-region" },
  { name: "Security-Widefield", tier: "pikes-peak", x: 440, y: 440, direction: "S of HQ", drive: "~18 min", anchor: "pikes-peak-region" },
  { name: "Manitou Springs", tier: "pikes-peak", x: 230, y: 325, direction: "W of HQ", drive: "~18 min", anchor: "pikes-peak-region" },

  // ── 30-mile extended reach ───────────────────────────────
  { name: "Woodland Park", tier: "extended", x: 155, y: 255, direction: "W of HQ", drive: "~35 min", anchor: "extended-reach" },
  { name: "Cañon City", tier: "extended", x: 130, y: 490, direction: "SW of HQ", drive: "~60 min", anchor: "extended-reach" },
  { name: "Castle Rock", tier: "extended", x: 415, y: 55, direction: "N of HQ", drive: "~45 min", anchor: "extended-reach" },
  { name: "Pueblo", tier: "extended", x: 420, y: 555, direction: "S of HQ", drive: "~45 min", anchor: "extended-reach" },
  { name: "Limon", tier: "extended", x: 755, y: 195, direction: "E of HQ", drive: "~75 min", anchor: "extended-reach" },
];

export const TIER_LABELS: Record<RingTier, string> = {
  "in-city": "In Colorado Springs",
  "eastern-plains": "Eastern El Paso County",
  "pikes-peak": "Pikes Peak Region",
  "extended": "30-Mile Extended Reach",
};

export const TIER_ANCHORS: Record<RingTier, string> = {
  "in-city": "in-city",
  "eastern-plains": "eastern-plains",
  "pikes-peak": "pikes-peak-region",
  "extended": "extended-reach",
};
